import type {
  CulturesPageRequest,
  CulturesPageResponse,
  ObservationMetric,
} from '~/api/backend/pages/culturesPageAPI'
import type { DashboardPageResponse } from '~/api/backend/pages/dashboardPageAPI'
import type { PlatePageResponse } from '~/api/backend/pages/platePageAPI'
import {
  isWellImagingObservation,
  type WellPage_PastObservation,
  type WellPageResponse,
} from '~/api/backend/pages/wellPageAPI'
import type { ClePlate } from '~/pages/Cle2025/clePlates/ClePlate.interface'
import { getPlateFormatFromDims } from '~/utils/microplate'
import type { DemoDerivedRecordsByKind } from './demoData'

const demoWellCellConcentration = (well: {
  cell_concentration?: number | null
}): number | null => {
  return well.cell_concentration ?? null
}

/**
 * Prepares query functions for all demoable pages.
 */
export function getDemoQueries(derived: DemoDerivedRecordsByKind) {
  return {
    CulturesPage(
      request: CulturesPageRequest,
      _metrics?: ObservationMetric[],
    ): CulturesPageResponse {
      // Note: Metrics parameter is intentionally ignored in demo data.
      // Demo data always includes both confluence and cell_concentration
      // regardless of requested metrics, simplifying demo data generation.
      let filteredPlates = derived.plates.map(plate => ({
        id: plate.id,
        barcode: plate.barcode,
        isCheckedIn: plate.isCheckedIn,
        firstCheckedInAt: plate.firstCheckedInAt,
        lastCheckedOutAt: plate.isCheckedIn
          ? null
          : plate.checkedOutAt || new Date().toISOString(),
        lastGeneratedAt: plate.firstCheckedInAt,
        averageConfluence:
          plate.wellCultures.length > 0
            ? plate.wellCultures
                .filter(wc => wc.confluence != null)
                .reduce(
                  (sum, wc, _, arr) => sum + (wc.confluence?.value ?? 0) / arr.length,
                  0,
                )
            : null,
        // TODO: Cultures page schema still includes averageCellConcentration even
        // though dashboard no longer uses it; keep explicit null until API drops field.
        averageCellConcentration: null,
        plateDimensions: plate.plateDimensions,
        wellCultures: plate.wellCultures.map(wc => ({
          well: wc.well,
          custom_culture_status: { category: wc.custom_culture_status.category },
          confluence: wc.confluence?.value ?? null,
        })),
      }))

      // Apply filters from the request
      for (const filter of request.plate_filters) {
        if (
          filter.field === 'plate_barcode' &&
          filter.operator.operator_type === 'contains_string'
        ) {
          filteredPlates = filteredPlates.filter(plate =>
            plate.barcode.toLowerCase().includes(filter.operator.value.toLowerCase()),
          )
        } else if (
          filter.field === 'plate_status' &&
          filter.operator.operator_type === 'is_any_of_plate_status'
        ) {
          filteredPlates = filteredPlates.filter(plate => {
            const plateStatus = plate.isCheckedIn ? 'checked_in' : 'checked_out'
            return filter.operator.values.includes(plateStatus)
          })
        } else if (
          filter.field === 'first_checked_in_at' &&
          filter.operator.operator_type === 'is_between_datetime_range'
        ) {
          filteredPlates = filteredPlates.filter(plate => {
            const plateDate = new Date(plate.firstCheckedInAt)
            const lowerDate = filter.operator.lower_datetime
              ? new Date(filter.operator.lower_datetime)
              : null
            const upperDate = filter.operator.upper_datetime
              ? new Date(filter.operator.upper_datetime)
              : null

            if (lowerDate && plateDate < lowerDate) return false
            if (upperDate && plateDate > upperDate) return false
            return true
          })
        } else if (
          filter.field === 'last_checked_out_at' &&
          filter.operator.operator_type === 'is_between_datetime_range'
        ) {
          filteredPlates = filteredPlates.filter(plate => {
            if (!plate.lastCheckedOutAt) return false
            const plateDate = new Date(plate.lastCheckedOutAt)
            const lowerDate = filter.operator.lower_datetime
              ? new Date(filter.operator.lower_datetime)
              : null
            const upperDate = filter.operator.upper_datetime
              ? new Date(filter.operator.upper_datetime)
              : null

            if (lowerDate && plateDate < lowerDate) return false
            if (upperDate && plateDate > upperDate) return false
            return true
          })
        }
      }

      return {
        filteredCulturePlates: filteredPlates,
      }
    },

    Dashboard(): DashboardPageResponse {
      const activePlates = derived.plates.filter(plate => plate.isCheckedIn)
      const recentlyInactivePlates = derived.plates.filter(plate => !plate.isCheckedIn)

      return {
        activePlates: activePlates.map(plate => ({
          id: plate.id,
          barcode: plate.barcode,
          isCheckedIn: plate.isCheckedIn,
          checkedOutAt: null,
          plateDimensions: plate.plateDimensions,
          wellCultures: plate.wellCultures.map(wc => ({
            well: wc.well,
            custom_culture_status: { category: wc.custom_culture_status.category },
            confluence: wc.confluence ? { value: wc.confluence.value } : null,
            cell_concentration: demoWellCellConcentration(
              wc as { cell_concentration?: number | null },
            ),
          })),
        })),
        recentlyInactivePlates: recentlyInactivePlates.map(plate => ({
          id: plate.id,
          barcode: plate.barcode,
          isCheckedIn: plate.isCheckedIn,
          checkedOutAt: plate.checkedOutAt || new Date().toISOString(),
          plateDimensions: plate.plateDimensions,
          wellCultures: plate.wellCultures.map(wc => ({
            well: wc.well,
            custom_culture_status: { category: wc.custom_culture_status.category },
            confluence: wc.confluence ? { value: wc.confluence.value } : null,
            cell_concentration: demoWellCellConcentration(
              wc as { cell_concentration?: number | null },
            ),
          })),
        })),
      }
    },

    PlatePage({ id }: { id: string }): PlatePageResponse {
      return { culturePlate: derived.plates.find(p => p.id === id) ?? null }
    },

    WellPage({
      plateId,
      position,
    }: {
      plateId: string
      position: string
    }): WellPageResponse {
      return {
        culture:
          derived.wells.find(
            w => w.culturePlate.id === plateId && w.well === position,
          ) ?? null,
      }
    },

    ClePipeline(): ClePlate[] {
      return derived.plates.map(plate => {
        const format = getPlateFormatFromDims({
          numRows: plate.plateDimensions.rows,
          numCols: plate.plateDimensions.columns,
        })
        return {
          id: plate.barcode,
          plateFormat: format,
          hasAlert: format.includes('24') && plate.plateObservationHistory.length > 2,
          cellLine: {
            cellLineIndex: 1,
            parentCellLine: plate.barcode.split('_')[0],
            desiredEdit: plate.barcode.split('_')[1],
            name: plate.barcode.substring(0, plate.barcode.lastIndexOf('_')),
          },
          phase: plate.barcode.endsWith('_1')
            ? 'seeding'
            : plate.barcode.includes('_2')
              ? '24wp_expansion'
              : '6wp_expansion',
          plateNumber: 1,
          wells: Object.fromEntries(
            plate.wellCultures.map(well => {
              if (!('observationHistory' in well)) {
                console.error(
                  'demoQueries: CLE demo got wells without observation history',
                )
                return []
              }
              const lastObs = (
                well.observationHistory as WellPage_PastObservation[]
              ).at(-1)
              let confluency = 0
              let image: string | null = null
              if (lastObs && isWellImagingObservation(lastObs)) {
                confluency = lastObs.confluence ?? 0
                image = lastObs.montage?.images?.[0].imageUrl ?? null
              }
              return [
                well.well,
                {
                  confluency,
                  image,
                },
              ]
            }),
          ),
          owner: 'Dr. Jimmy Sastra',
          status: plate.isCheckedIn ? 'Active' : 'Terminated',
        }
      })
    },
  }
}

export type DemoQueries = ReturnType<typeof getDemoQueries>
